import React, { useState } from 'react';
import { StudentDetail } from '../types';
import { studentService } from '../services/studentService';

interface StudentCodePageProps {
  onBack: () => void;
  onStudentFound: (student: StudentDetail) => void;
}

type SearchType = 'student_code' | 'student_id';

const StudentCodePage: React.FC<StudentCodePageProps> = ({
  onBack,
  onStudentFound,
}) => {
  const [searchType, setSearchType] = useState<SearchType>('student_id');
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!inputValue.trim()) {
      setError(`Please enter a ${searchType === 'student_code' ? 'student code' : 'student ID'}`);
      return;
    }

    setIsLoading(true);
    try {
      const student = searchType === 'student_code' 
        ? await studentService.getStudentByCode(inputValue)
        : await studentService.getStudentById(inputValue);
      
      if (student) {
        onStudentFound(student);
      } else {
        setError(`${searchType === 'student_code' ? 'Student code' : 'Student ID'} not found. Please check and try again.`);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 md:pb-32">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white p-6 shadow-lg">
        <div className="w-full px-4 md:max-w-6xl md:mx-auto">
          <h1 className="text-3xl font-bold">Search Student</h1>
          <p className="text-primary-100">Search for fee details using student code or ID</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-2 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit}>
            {/* Search Type Selection - Radio Button Cards */}
            <div className="mb-6">
              <label className="block text-gray-700 font-bold mb-4">
                Select Search Type <span className="text-red-600">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Student ID Card */}
                <button
                  type="button"
                  onClick={() => {
                    setSearchType('student_id');
                    setInputValue('');
                    setError('');
                  }}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    searchType === 'student_id'
                      ? 'border-secondary-600 bg-secondary-50 shadow-md'
                      : 'border-gray-300 hover:border-secondary-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={searchType === 'student_id'}
                      onChange={() => {}}
                      className="w-5 h-5 mt-0.5 flex-shrink-0 text-secondary-600"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 mb-1">Student ID</p>
                      <p className="text-sm text-gray-600">e.g., 13/18</p>
                    </div>
                  </div>
                </button>

                {/* Student Code Card */}
                <button
                  type="button"
                  onClick={() => {
                    setSearchType('student_code');
                    setInputValue('');
                    setError('');
                  }}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    searchType === 'student_code'
                      ? 'border-primary-600 bg-primary-50 shadow-md'
                      : 'border-gray-300 hover:border-primary-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={searchType === 'student_code'}
                      onChange={() => {}}
                      className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary-600"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 mb-1">Student Code</p>
                      <p className="text-sm text-gray-600">e.g., STU001</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Input Field */}
            <div className="mb-6">
              <label className="block text-gray-700 font-bold mb-2">
                {searchType === 'student_code' ? 'Student Code' : 'Student ID'} <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => {
                  const value = e.target.value;
                  // For student_code, convert to uppercase; for student_id, keep as is
                  setInputValue(searchType === 'student_code' ? value.toUpperCase() : value);
                  setError('');
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary-600 text-lg transition-colors duration-200"
                disabled={isLoading}
              />
              <p className="text-gray-500 text-sm mt-2">
                {searchType === 'student_code' 
                  ? 'Enter the student code provided by the school'
                  : 'Enter the student ID (format: XX/XX)'}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-600 p-4 rounded">
                <p className="text-red-700 font-semibold">{error}</p>
              </div>
            )}

            {/* Search Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>

        </div>
      </div>

      {/* Fixed Bottom Action Bar (Desktop only, floating on mobile) */}
      <div className="md:fixed md:bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl z-50 mt-8 md:mt-0">
        <div className="max-w-7xl mx-auto px-2 py-4">
          <div className="flex gap-4 max-w-2xl mx-auto">
            <button
              onClick={onBack}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-4 px-6 rounded-lg transition-colors duration-200 text-lg"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentCodePage;
